import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import RoundedButton from './button';
import {
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Table from '@mui/material/Table';
import { MyContext } from './MyContext';
import { CloseSharp } from '@mui/icons-material';
import Joi from 'joi';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { sendEmail } from './email';
import { currencyFormat } from './utils';
import { useEffect } from 'react';

const schema = Joi.object({
  title: Joi.string()
    .valid('mr', 'mrs')
    .required()
    .messages({ 'any.required': 'Title is required.' }),

  firstName: Joi.string().allow(null, '').optional(),
  lastName: Joi.string().min(1).max(255).required().messages({
    'string.empty': 'Last Name is required.',
    'any.required': 'Last Name is required.',
  }),

  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .messages({
      'string.empty': 'Email is required.',
      'any.required': 'Email required.',
    }),
  zipCode: Joi.string().allow(null, '').optional(),
  phone: Joi.string().allow(null, '').optional(),
  country: Joi.string().allow(null, '').optional(),
  city: Joi.string().allow(null, '').optional(),
  street: Joi.string().allow(null, '').optional(),
  streetNo: Joi.string().allow(null, '').optional(),
  agreement: Joi.boolean().default(false),
});

const ContactForm = () => {
  const { canvasImg, handleClose, open, cart, storeData } =
    React.useContext(MyContext);
  const [errorMessage, setErrorMessage] = React.useState('');
  const formRef = React.useRef(null);

  const total = React.useMemo(
    () => cart.reduce((sum, item) => sum + item.price, 0),
    [cart]
  );

  const style = {
    overflow: 'auto',
    width: '100%',
    bgColor: '#fff',
    p: 4,
    border: 0,
    outline: 0,
    borderRadius: 3,
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(schema),
  });

  useEffect(() => {
    console.log(open);
  }, [open]);

  const onSubmit = async data => {
    if (!data.agreement)
      return setErrorMessage('Check license & agreement to proceed');

    setErrorMessage('');

    try {
      var mailtomail = storeData.email;

      var type = 'reseller';
      const result = await sendEmail({
        ...data,
        logolink: storeData.file,
        mail: data.email,
        canvasImg,
        products: cart.map(item => ({
          articlename: item.name,
          articlenumber: item.productNumber,
          quantity: item.qty,
          price: item.price.toString(),
        })),
        total,
        type,
        mailtomail,
      });
      console.log(result);

      type = 'customer';
      mailtomail = data.email;

      const result2 = await sendEmail({
        ...data,
        logolink: storeData.file,
        mail: data.email,
        canvasImg,
        products: cart.map(item => ({
          articlename: item.name,
          articlenumber: item.productNumber,
          quantity: item.qty,
          price: item.price.toString(),
        })),
        total,
        type,
        mailtomail,
      });

      console.log(result2);
    } catch (ex) {
      console.log(ex.message);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth
        scroll={'paper'}
        aria-labelledby='scroll-dialog-title'
        aria-describedby='scroll-dialog-description'
      >
        <DialogContent dividers>
          <Box>
            <IconButton
              onClick={handleClose}
              sx={{ position: 'absolute', right: 3, top: 3 }}
            >
              <CloseSharp />
            </IconButton>
            <Grid container spacing={4}>
              <Grid item md={6} xs={12}>
                <Typography variant='h5' component='h2' fontWeight={600} mb={2}>
                  Request a quote
                </Typography>

                <Box
                  component={'img'}
                  src={canvasImg}
                  sx={{
                    width: '100%',
                    border: 'solid #000 1px',
                    objectFit: 'cover',
                  }}
                />

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Product Number</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Price</TableCell>
                    </TableHead>
                    <TableBody>
                      {cart.map(row => (
                        <TableRow key={row.productNumber}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.productNumber}</TableCell>
                          <TableCell>{row.qty}</TableCell>
                          <TableCell>{currencyFormat(row.price)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={3}>Total</TableCell>
                        <TableCell align='right'>
                          {currencyFormat(total)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  id='modal-modal-title'
                  variant='h5'
                  component='h2'
                  fontWeight={600}
                  mb={2}
                >
                  Contact Information
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
                  <Grid container spacing={2} mb={2}>
                    <Grid item md={4} sm={12} xs={12}>
                      <FormControl fullWidth>
                        <InputLabel id='demo-simple-select-label'>
                          Mr. / Mrs.
                        </InputLabel>
                        <Select
                          label='Mr / Mrs.'
                          {...register('title')}
                          error={errors?.title?.message}
                          helperText={errors?.title?.message}
                        >
                          <MenuItem value={'mr'}>Mr.</MenuItem>
                          <MenuItem value={'mrs'}>Mrs.</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item md={4} sm={12} xs={12}>
                      <TextField
                        label='First Name'
                        variant='outlined'
                        {...register('firstName')}
                        error={errors?.firstName?.message}
                        helperText={errors?.firstName?.message}
                      />
                    </Grid>
                    <Grid item md={4} sm={12} xs={12}>
                      <TextField
                        label='Last Name'
                        variant='outlined'
                        {...register('lastName')}
                        error={errors?.lastName?.message}
                        helperText={errors?.lastName?.message}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2} sm={12} xs={12}>
                    <Grid item md={8}>
                      <TextField
                        label='Street'
                        variant='outlined'
                        fullWidth
                        {...register('street')}
                        error={errors?.street?.message}
                        helperText={errors?.street?.message}
                      />
                    </Grid>
                    <Grid item md={4} sm={12} xs={12}>
                      <TextField
                        label='Street No'
                        variant='outlined'
                        {...register('streetNo')}
                        error={errors?.streetNo?.message}
                        helperText={errors?.streetNo?.message}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2}>
                    <Grid item md={4} sm={12} xs={12}>
                      <TextField
                        label='Zip Code'
                        variant='outlined'
                        {...register('zipCode')}
                        error={errors?.zipCode?.message}
                        helperText={errors?.zipCode?.message}
                      />
                    </Grid>
                    <Grid item md={8} sm={12} xs={12}>
                      <TextField
                        fullWidth
                        label='City'
                        variant='outlined'
                        {...register('city')}
                        error={errors?.city?.message}
                        helperText={errors?.city?.message}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} mb={2}>
                    <Grid item md={12} sm={12} xs={12}>
                      <TextField
                        label='Country'
                        variant='outlined'
                        fullWidth
                        {...register('country')}
                        error={errors?.country?.message}
                        helperText={errors?.country?.message}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2}>
                    <Grid item md={4} sm={12} xs={12}>
                      <TextField
                        label='Email'
                        variant='outlined'
                        {...register('email')}
                        error={errors?.email?.message}
                        helperText={errors?.email?.message}
                      />
                    </Grid>
                    <Grid item md={8} sm={12} xs={12}>
                      <TextField
                        label='Phone'
                        variant='outlined'
                        fullWidth
                        {...register('phone')}
                        error={errors?.phone?.message}
                        helperText={errors?.phone?.message}
                      />
                    </Grid>
                  </Grid>
                  <Stack
                    direction='row'
                    justifyContent={'start'}
                    alignItems={'center'}
                    mb={2}
                  >
                    <Checkbox {...register('agreement')} />
                    <Typography variant='body2'>
                      By clicking I agree to the terms and conditions of the
                      contract
                    </Typography>
                  </Stack>
                  <RoundedButton>Submit</RoundedButton>
                  {errorMessage && (
                    <Typography variant='body2' color={'red'}>
                      {errorMessage}
                    </Typography>
                  )}
                </form>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContactForm;
